import React, { useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-routing-machine';
import { Map, TileLayer, MapLayer, LayerGroup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { makeStyles } from '@material-ui/core/styles';
import ClinicianLocationMarker from './ClinicianLocationMarker';
import AssignmentLocationMarker from './AssignmentLocationMarker';
import LoadingPageText from '../../common/Loading/LoadingPageText';
import { CardItemsColumnHtml } from './ClinicianList';
import { useFetchMapViewDetails } from '../../../actions/areas/planner';
import globals from '../../../globals/communityGlobals';
import { getElementPropsByClass } from '../timeline/timelineHelper';

import { SearchFilter } from '../Filters/SearchFilter';
import { Filters } from '../Filters/Filters';
import { SortOptionsDialog } from '../Filters/SortOptionsDialog';
import { sortClinicianOptions, sortingIconMap } from '../timeline/timelineConstants';

import _ from '../../../utils/lodash';
import { VisitType } from '../../../enums';

const useStyles = makeStyles({
  clinicianPopModal: {
    backgroundColor: '#333333',
    color: '#fff',
    padding: '1px',
    minWidth: '135px',
  },
});

class RoutingMachine extends MapLayer {
  getWaypoints = locations =>
    locations.filter(l => !!l).map(location => L.latLng(location.latitude, location.longitude));

  createLeafletElement() {
    const { locations } = this.props;
    const waypoints = this.getWaypoints(locations);
    return L.Routing.control({
      router: L.Routing.osrmv1({
        serviceUrl: `${globals.urls.apiUrl}/OSRMProxy/Route`,
      }),
      waypoints,
      createMarker: () => false,
      addWaypoints: false,
      draggableWaypoints: false,
    })
      .addTo(this.context.map)
      .getPlan();
  }

  updateLeafletElement(fromProps, toProps) {
    if (
      fromProps.locations.length !== toProps.locations.length ||
      fromProps.locations
        .filter(e => !!e)
        .map(r => r.name)
        .join() !==
        toProps.locations
          .filter(e => !!e)
          .map(r => r.name)
          .join()
    ) {
      const waypoints = this.getWaypoints(toProps.locations);

      this.leafletElement.setWaypoints(waypoints);
    }
  }
}

export function MapViewPage() {
  const [forceRefresh] = useState(false);
  const [activeClinician, setActiveClinician] = useState({ id: null, postingId: null });
  const classes = useStyles();
  const [clinicianFilterPhrase, setClinicianFilterPhrase] = useState('');
  const [clinciianFilterOption, setClinciianFilterOption] = useState(1);
  const [clinciianSortingOptions, setClinicianSortingOptions] = useState(sortClinicianOptions);
  const [showMoreAnchor, setShowMoreAnchor] = useState(null);
  const { data: timeLineData, isDataLoading } = useFetchMapViewDetails(forceRefresh);

  const mapView = useRef();

  if (isDataLoading)
    return <LoadingPageText showHeader={false}>Wait a moment while we gather timeline info...</LoadingPageText>;

  const filteredData = _.filter(
    timeLineData.data,
    x =>
      clinicianFilterPhrase === '' ||
      x.clinician.personName.toLowerCase().indexOf(clinicianFilterPhrase) !== -1 ||
      x.clinician.staffNumber.startsWith(clinicianFilterPhrase),
  );

  const getDayPlanLayer = () => {
    let assignments = null;
    let locations = [];
    const selectedClinician = filteredData.find(
      el => el.clinician.id === activeClinician.id && el.clinician.postingId === activeClinician.postingId,
    );

    if (selectedClinician) {
      if (selectedClinician.clinician.clinicianLocation) {
        mapView.current.leafletElement.flyTo([
          selectedClinician.clinician.clinicianLocation.latitude,
          selectedClinician.clinician.clinicianLocation.longitude,
        ]);
      }

      if (selectedClinician.assignments.length > 0) {
        assignments = selectedClinician.assignments
          .filter(r => !!r.location && r.visitType !== VisitType.PhoneCall)
          .map((assignment, index) => (
            <AssignmentLocationMarker
              key={`assignmentMarker${assignment.id}`}
              orderMark={String.fromCharCode(index + 65)}
              assignment={assignment}
            />
          ));
        locations = [
          selectedClinician.clinician.clinicianLocation,
          ...selectedClinician.assignments
            .filter(a => !!a && a.visitType !== VisitType.PhoneCall)
            .map(assignment => assignment.location),
        ];
      }
    }

    return (
      <LayerGroup key={'dayPlanLayer'}>
        <RoutingMachine locations={locations} />
        <MarkerClusterGroup>{assignments}</MarkerClusterGroup>
      </LayerGroup>
    );
  };

  const setFilterValue = (name, value) => {
    setClinciianFilterOption(value);
    const sortedList = clinciianSortingOptions.map(cl => {
      if (cl.id === value) {
        return {
          ...cl,
          name: (
            <span>
              {`✓ ${cl.label}`} <i className={!cl.isAsc ? sortingIconMap.ASC : sortingIconMap.DESC} />
            </span>
          ),
          isAsc: !cl.isAsc,
        };
      }
      return {
        ...cl,
        name: cl.label,
        isAsc: false,
      };
    });
    setClinicianSortingOptions(sortedList);
  };

  // Sorting the filtered data source
  const selectedSortingOption = clinciianSortingOptions.find(o => o.id === clinciianFilterOption);
  const isAsc = selectedSortingOption.isAsc;

  filteredData.sort((a, b) => {
    const propertyName = selectedSortingOption.propertyName;
    if (isAsc && a.clinician[propertyName] > b.clinician[propertyName]) return 1;
    if (!isAsc && a.clinician[propertyName] < b.clinician[propertyName]) return 1;
    if (isAsc && a.clinician[propertyName] < b.clinician[propertyName]) return -1;
    if (!isAsc && a.clinician[propertyName] > b.clinician[propertyName]) return -1;
    return 0;
  });

  const markers = filteredData
    .filter(r => !!r.clinician.clinicianLocation)
    .map(({ ...props }) => (
      <ClinicianLocationMarker
        clinician={props.clinician}
        isMileageCapEnabled={timeLineData.isMileageCapEnabled}
        key={`clinicianMarker${props.clinician.id}`}
      />
    ));

  const defaultZoom = 10;
  const defaultCenter = [51.505, -0.09];
  const heightOffset = 70 + getElementPropsByClass('page-header').height;
  const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  const minHeight = windowHeight - heightOffset;
  document.title = 'Planner Map view';

  return (
    <React.Fragment>
      <section className="mapview-filters-section">
        <div className="form-map-input">
          <Filters bands={timeLineData.clinicianBands} />
        </div>
      </section>
      <div className="mapView-content-section">
        <div id="timeline-schedule" className="total-wrapper margin-top-mapView">
          {/* Left static column showing 'Clinicians Cards' */}

          <div className="timeline-cards-container mapView-cards" style={{ maxHeight: `${minHeight}px` }}>
            <div className="timeline-input-wrapper">
              <div className="timeline-form-map-input">
                <SearchFilter
                  width="100%"
                  filterKey={'clinician'}
                  label="Find Clinicians"
                  fullWidth
                  filterValue={clinicianFilterPhrase}
                  onChange={(name, value) => setClinicianFilterPhrase(value)}
                />
                <span className="icon-extend icon-search icon-small" />
              </div>

              <div className="form-map-icon">
                <span
                  className="icon-extend icon-sorting icon-small"
                  role="button"
                  tabIndex={0}
                  onClick={event => setShowMoreAnchor(event.target)}
                />
                <SortOptionsDialog
                  clinicianModal={classes.clinicianPopModal}
                  open={showMoreAnchor !== null}
                  anchorEl={showMoreAnchor}
                  handleClose={() => setShowMoreAnchor(null)}
                  width="100%"
                  fullWidth
                  data={clinciianSortingOptions}
                  filterKey={'sortClinicianFilter'}
                  filterValue={clinciianFilterOption || sortClinicianOptions[0].id}
                  onChange={(name, value) => setFilterValue(name, value)}
                />
              </div>
            </div>

            <CardItemsColumnHtml
              items={filteredData}
              isMileageCapEnabled={timeLineData.isMileageCapEnabled}
              activeClinician={activeClinician}
              setActiveClinician={setActiveClinician}
            />
          </div>

          {/* Timeline scheduler grid content */}
          <div className="timeline-container timeline-block rest-columns">
            <Map
              className="markercluster-map"
              useFlyTo
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ width: '100%', minHeight: `${minHeight}px` }}
              ref={mapView}
            >
              <TileLayer attribution="" url={`${globals.urls.apiUrl}/OpenStreetMapProxy/GetTile/{z}/{x}/{y}`} />
              {getDayPlanLayer()}
              <MarkerClusterGroup>{markers}</MarkerClusterGroup>
            </Map>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
